import {
  PHASE_KEYS,
  formatMoney,
  siteWords,
  type ArtDto,
  type CostBar,
  type LeadIntakeDto,
  type PipelineDto,
  type PipelineItemDto,
} from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { api, errorText, queryClient, type ApiError } from '../api.ts';
import { MiniBar } from '../components/CostBar.tsx';
import { useFeedbackRecord } from '../components/feedback-context.ts';
import { ProjectStatusTag } from '../components/ProjectStatus.tsx';
import { statusTag } from '../components/statusTag.ts';
import { TestTag } from '../components/TestProject.tsx';
import { TEST_PROJECT_WORDS } from '../components/testProjectWords.ts';
import { ErrorLine, LoadError, Loading, Page, Tag, TextLink, Title } from '../components/ui.tsx';
import { isStaff, useMe } from '../session.tsx';
import { dateWords } from '../days.ts';

export function Home() {
  const { data: me } = useMe();
  if (!me) return null;
  if (isStaff(me)) return <Pipeline />;
  // The sub portal arrives in phase 4 (spec §16). Until then this is all a sub has.
  if (me.viewAs === 'sub' || (me.projects.length > 0 && me.projects.every((p) => p.role === 'sub'))) {
    return (
      <Page>
        <Title>Sub portal</Title>
        <p className="text-ink-2">There is no sub portal yet: nothing is shared with subs here.</p>
      </Page>
    );
  }
  if (me.projects.length === 1) return <Navigate to={`/p/${me.projects[0]!.id}`} replace />;
  return (
    <Page>
      <Title>Your projects</Title>
      {me.projects.length === 0 ? (
        <p className="text-ink-2">There is nothing here for this account yet.</p>
      ) : (
        <ul className="divide-y divide-rule border-y border-rule">
          {me.projects.map((p) => (
            <li key={p.id} className="py-4">
              <Link to={`/p/${p.id}`} className="font-display text-[1.5rem] hover:text-moss-deep">
                {p.name}
              </Link>
              {p.isSample ? (
                <span className="ml-3">
                  <TestTag />
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}

/**
 * Contractor home, Projects (spec §8; Jason, 2026-10-02, and 2026-10-03:
 * "change the 'pipeline' to 'projects'"): the projects in progress, an
 * architect's register with each one's small watercolor, and beside them the
 * leads, with the address inquiries are forwarded to and whatever it has
 * brought in, waiting for a yes. On a wide screen the register spreads into
 * columns: where each stands, and what is billed and paid.
 */
function Pipeline() {
  const [samples, setSamples] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const { data, error, isLoading } = useQuery<PipelineDto, ApiError>({
    queryKey: ['pipeline', samples],
    queryFn: () => api<PipelineDto>(`/projects${samples ? '?samples=1' : ''}`),
    // Icons are being painted: look again until they are in.
    refetchInterval: (q) => (q.state.data?.painting ? 10_000 : false),
  });
  useFeedbackRecord(null, null);
  if (error) return <LoadError error={error} />;
  return (
    <Page wide>
      <Title kicker="Projects">Work in hand</Title>
      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="mt-8 grid gap-x-14 gap-y-16 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <section>
            <div className="flex items-baseline justify-between border-b border-ink/40 pb-2">
              <h2 className="text-[1.375rem] sm:text-[1.5rem]">In progress</h2>
              <TextLink to="/leads/new?project=1" className="text-sm text-ink-2 hover:text-ink">
                New project
              </TextLink>
            </div>
            {data.projects.length === 0 ? (
              <p className="py-4 text-ink-3">Nothing in progress. Start one with New project, or from a lead.</p>
            ) : (
              <>
                <RegisterHead />
                {/* By where each is, from its process (Jason, 2026-10-06; Still to decide, A): the phases in order. */}
                {byStage(data.projects).map(({ name, items }) => (
                  <section key={name} aria-label={name}>
                    <p className="label mt-5 border-b border-rule pb-1">
                      {name} · <span className="num">{items.length}</span>
                    </p>
                    <ul className="divide-y divide-rule border-b border-rule">
                      {items.map((p) => (
                        <ProjectRow key={p.id} item={p} />
                      ))}
                    </ul>
                  </section>
                ))}
              </>
            )}
            {data.ended.length ? (
              <div className="mt-6">
                <button type="button" aria-expanded={showEnded} className="label min-h-11 hover:!text-ink" onClick={() => setShowEnded(!showEnded)}>
                  {showEnded ? 'Hide' : 'Show'} ended ({data.ended.length})
                </button>
                {showEnded ? (
                  <>
                    <RegisterHead />
                    <ul className="divide-y divide-rule border-y border-rule xl:border-t-0">
                      {data.ended.map((p) => (
                        <ProjectRow key={p.id} item={p} />
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            ) : null}
            <label className="label mt-8 flex min-h-11 items-center gap-2.5">
              <input type="checkbox" checked={samples} onChange={(e) => setSamples(e.target.checked)} />
              {TEST_PROJECT_WORDS.show}
            </label>
          </section>
          <Leads leads={data.leads} />
        </div>
      )}
    </Page>
  );
}

/** The projects in progress by where each is, the phases in their order; one with nothing on its process yet last. */
function byStage(items: PipelineItemDto[]): { name: string; items: PipelineItemDto[] }[] {
  const groups = new Map<string, { rank: number; name: string; items: PipelineItemDto[] }>();
  for (const p of items) {
    const key = p.stage?.key ?? 'none';
    const g = groups.get(key) ?? { rank: p.stage ? PHASE_KEYS.indexOf(p.stage.key) : PHASE_KEYS.length, name: p.stage?.name ?? 'Nothing on its process yet', items: [] };
    g.items.push(p);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => a.rank - b.rank);
}

/** A project's small watercolor, or an empty frame of the same size until it is painted. */
function Thumb({ icon, small = false }: { icon: ArtDto | null; small?: boolean }) {
  const size = small ? 'h-9 w-[3.375rem]' : 'h-12 w-[4.5rem]';
  if (!icon) return <span aria-hidden="true" className={`${size} block shrink-0 rounded-[2px] bg-inset`} />;
  return (
    <img
      src={`${icon.url}?w=160`}
      srcSet={icon.widths.map((w) => `${icon.url}?w=${w} ${w}w`).join(', ')}
      sizes={small ? '54px' : '72px'}
      alt=""
      loading="lazy"
      decoding="async"
      width={small ? 54 : 72}
      height={small ? 36 : 48}
      className={`${size} block shrink-0 rounded-[2px] object-cover`}
    />
  );
}

/** The small bar in words, for a screen reader. */
function miniLabel(bar: CostBar): string {
  return `${formatMoney(bar.billedCents)} billed and ${formatMoney(bar.moneyInCents)} paid`;
}

/** The register's columns on a wide screen: picture, project, next, the money bar small (the rows repeat it after xl:). */
const REGISTER = 'grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,17rem)_10rem] gap-x-5';

/** The columns' names, on a wide screen only, where the register spreads into them. */
function RegisterHead() {
  return (
    <div className={`label hidden border-b border-rule py-2 xl:grid ${REGISTER}`} aria-hidden="true">
      <span />
      <span>Project</span>
      <span>Next</span>
      <span>Billed and paid</span>
    </div>
  );
}

/** The register: picture, name, where it stands, and the money in its own column. Blank money stays blank. */
function ProjectRow({ item }: { item: PipelineItemDto }) {
  const meta = [item.contactName, item.siteAddress ? siteWords(item.siteAddress) : null].filter(Boolean).join(' · ');
  return (
    <li>
      <Link
        to={`/p/${item.id}`}
        className="group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 py-3.5 sm:gap-x-5 xl:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,17rem)_10rem]"
      >
        <Thumb icon={item.icon} />
        <span className="min-w-0">
          {/* Each piece keeps its space after it, never before: a tag wrapped onto a line of its own starts at the line's edge. */}
          <span className="mr-2.5 font-display text-[1.25rem] leading-snug group-hover:text-moss-deep">{item.name}</span>
          {item.isSample ? (
            <span className="mr-2.5">
              <TestTag />
            </span>
          ) : null}
          {statusTag(item.status) ? (
            <span className="mr-2.5">
              <ProjectStatusTag status={item.status} />
            </span>
          ) : null}
          {meta ? <span className="meta block truncate">{meta}</span> : null}
          {item.summary ? <span className="block truncate text-[0.875rem] text-ink-2">{item.summary}</span> : null}
        </span>
        {/* Narrower screens: where it stands and its money, stacked at the right. */}
        <span className="text-right xl:hidden">
          {item.next ? <span className="label block">{item.next.gateName}</span> : null}
          {item.bar ? (
            <span className="ml-auto mt-1 block w-24">
              <MiniBar bar={item.bar} label={miniLabel(item.bar)} />
            </span>
          ) : null}
        </span>
        <span className="hidden min-w-0 xl:block">
          {item.next ? (
            <>
              <span className="label block">{item.next.gateName}</span>
              {item.next.stepName ? <span className="block truncate text-[0.875rem] text-ink-2">{item.next.stepName}</span> : null}
            </>
          ) : null}
        </span>
        <span className="hidden xl:block">{item.bar ? <MiniBar bar={item.bar} label={miniLabel(item.bar)} /> : null}</span>
      </Link>
    </li>
  );
}

/** The leads column: where to forward inquiries, what has come in to confirm, and the open leads by stage. */
function Leads({ leads }: { leads: PipelineItemDto[] }) {
  const { data: intake } = useQuery<LeadIntakeDto, ApiError>({
    queryKey: ['leads', 'intake'],
    queryFn: () => api<LeadIntakeDto>('/leads/intake'),
    // Emails being read into proposals are looked at again shortly; otherwise the column refreshes when the page comes
    // back into view, never on a timer (Jason's rule: no timer polling).
    refetchInterval: (q) => (q.state.data?.reading ? 5_000 : false),
  });
  return (
    <aside className="lg:border-l lg:border-rule lg:pl-10">
      <div className="flex items-baseline justify-between border-b border-ink/40 pb-2">
        <h2 className="text-[1.375rem] sm:text-[1.5rem]">Leads</h2>
        <TextLink to="/leads/new" className="text-sm text-ink-2 hover:text-ink">
          New lead
        </TextLink>
      </div>
      {intake ? <ForwardTo intake={intake} /> : null}
      {/* What intake could not settle by itself waits on its own page (spec "Intake" §5; design study 14, page A). */}
      {intake?.waiting ? (
        <p className="mt-5">
          <Link to="/intake" className="link">
            <span className="num">{intake.waiting}</span> email{intake.waiting === 1 ? '' : 's'} wait{intake.waiting === 1 ? 's' : ''} on you
          </Link>
        </p>
      ) : null}
      {leads.length === 0 ? <p className="mt-7 text-ink-3">No open leads.</p> : null}
      {/* A lead is a lead until it is started: no stages to choose (Still to decide, A). */}
      {leads.length ? (
        <section className="mt-7">
          <ul className="divide-y divide-rule border-y border-rule">
            {leads.map((l) => (
              <LeadRow key={l.id} item={l} />
            ))}
          </ul>
        </section>
      ) : null}
    </aside>
  );
}

const ago = (iso: string) => {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h ago` : dateWords(iso);
};

/** The address leads are forwarded to, plainly, with a way to copy it; or what it takes to have one. */
function ForwardTo({ intake }: { intake: LeadIntakeDto }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(intake.address!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const check = async () => {
    setBusy(true);
    setErr(null);
    try {
      queryClient.setQueryData(['leads', 'intake'], await api<LeadIntakeDto>('/leads/intake/check', { body: {} }));
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-5 border-b border-rule pb-4">
      <div className="label">Forward inquiries to</div>
      {intake.address ? (
        <p className="mt-1.5 flex flex-wrap items-center gap-x-4">
          <a href={`mailto:${intake.address}`} className="num break-all text-[1rem] text-ink hover:text-moss-deep">
            {intake.address}
          </a>
          <button type="button" className="link min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </p>
      ) : null}
      {intake.blocked ? (
        <p className="mt-1.5 text-[0.875rem] text-ink-2">
          {intake.blocked}{' '}
          <Link to="/settings/connections" className="link">
            Settings
          </Link>
        </p>
      ) : (
        <p className="meta mt-0.5 flex flex-wrap items-center gap-x-2">
          <span>{intake.reading ? `Reading ${intake.reading} email${intake.reading === 1 ? '' : 's'}…` : intake.checkedAt ? `Checked ${ago(intake.checkedAt)}` : 'Read every ten minutes'}</span>
          <span aria-hidden="true">·</span>
          <button type="button" className="link min-h-11 hover:!text-ink" disabled={busy} onClick={() => void check()}>
            {busy ? 'Checking…' : 'Check now'}
          </button>
        </p>
      )}
      <ErrorLine error={err} />
    </div>
  );
}

function LeadRow({ item }: { item: PipelineItemDto }) {
  const meta = [item.contactName, item.siteAddress ? siteWords(item.siteAddress) : null].filter(Boolean).join(' · ');
  return (
    <li>
      <Link to={`/p/${item.id}`} className="group flex items-center gap-3.5 py-3">
        <Thumb icon={item.icon} small />
        <span className="min-w-0">
          <span className="font-display text-[1.0625rem] leading-snug group-hover:text-moss-deep">{item.name}</span>
          {item.isSample ? (
            <span className="ml-2">
              <TestTag />
            </span>
          ) : null}
          {item.fromEmail ? (
            // Made from an email by itself, not opened yet (spec "Intake" §4).
            <span className="ml-2">
              <Tag tone="cedar">From email</Tag>
            </span>
          ) : null}
          {meta ? <span className="meta block truncate">{meta}</span> : null}
        </span>
      </Link>
    </li>
  );
}
